/**
 * Character Badge System
 *
 * Badges are subtle color + icon identifiers for party members.
 * Colors are muted and integrate with the dark fantasy aesthetic.
 * Players can choose their preferred badge color and icon from this palette.
 */

import type { IconName } from './Icon';
export type { IconName };

export interface Badge {
  id: string;
  name: string;
  /** Muted primary color - used for backgrounds */
  color: string;
  /** Subtle border/accent color */
  accent: string;
  /** Light text color for contrast */
  text: string;
  /** Optional icon override */
  icon?: IconName;
  /** Category for organization */
  category: 'earth' | 'water' | 'fire' | 'air' | 'arcane' | 'nature' | 'shadow' | 'metal';
}

/**
 * Badge palette - 24 options organized by elemental affinity
 * Colors are desaturated and dark to fit the Gould aesthetic
 */
export const BADGES: Badge[] = [
  // Earth tones - grounded, stable
  { id: 'clay', name: 'Clay', color: '#4a3f35', accent: '#6b5d4d', text: '#d4c4b0', category: 'earth' },
  { id: 'ochre', name: 'Ochre', color: '#5c4a32', accent: '#7d6344', text: '#e0d0b8', category: 'earth' },
  { id: 'umber', name: 'Umber', color: '#3d3028', accent: '#594538', text: '#c8b8a0', category: 'earth' },

  // Water tones - calm, deep
  { id: 'slate', name: 'Slate', color: '#3a4550', accent: '#4d5a66', text: '#b8c4d0', category: 'water' },
  { id: 'teal', name: 'Teal', color: '#2d4a4a', accent: '#3d5f5f', text: '#a8c8c8', category: 'water' },
  { id: 'midnight', name: 'Midnight', color: '#2a3545', accent: '#3a4860', text: '#a0b4d0', category: 'water' },

  // Fire tones - warm, muted
  { id: 'rust', name: 'Rust', color: '#4d3535', accent: '#663d3d', text: '#d0b0b0', category: 'fire' },
  { id: 'ember', name: 'Ember', color: '#4a3828', accent: '#664d38', text: '#d8c0a0', category: 'fire' },
  { id: 'wine', name: 'Wine', color: '#3d2838', accent: '#523848', text: '#c8a8b8', category: 'fire' },

  // Air tones - light, ethereal
  { id: 'storm', name: 'Storm', color: '#404550', accent: '#555d6a', text: '#c0c8d4', category: 'air' },
  { id: 'mist', name: 'Mist', color: '#454545', accent: '#5a5a5a', text: '#c8c8c8', category: 'air' },
  { id: 'dusk', name: 'Dusk', color: '#45404a', accent: '#5a5560', text: '#c8c4d0', category: 'air' },

  // Arcane tones - mystical, powerful
  { id: 'amethyst', name: 'Amethyst', color: '#3d3550', accent: '#504568', text: '#c0b4d8', category: 'arcane' },
  { id: 'indigo', name: 'Indigo', color: '#353d55', accent: '#455070', text: '#b4c0e0', category: 'arcane' },
  { id: 'void', name: 'Void', color: '#2d2d3d', accent: '#3d3d50', text: '#b0b0c8', category: 'arcane' },

  // Nature tones - organic, living
  { id: 'moss', name: 'Moss', color: '#354535', accent: '#455a45', text: '#b4c8b4', category: 'nature' },
  { id: 'fern', name: 'Fern', color: '#3a4838', accent: '#4d5d4a', text: '#b8ccb0', category: 'nature' },
  { id: 'sage', name: 'Sage', color: '#404840', accent: '#555f55', text: '#c4ccc4', category: 'nature' },

  // Shadow tones - mysterious, stealthy
  { id: 'obsidian', name: 'Obsidian', color: '#282828', accent: '#383838', text: '#a8a8a8', category: 'shadow' },
  { id: 'charcoal', name: 'Charcoal', color: '#2d2d30', accent: '#404045', text: '#b0b0b4', category: 'shadow' },
  { id: 'shadow', name: 'Shadow', color: '#252530', accent: '#353540', text: '#a0a0b0', category: 'shadow' },

  // Metal tones - refined, valuable
  { id: 'bronze', name: 'Bronze', color: '#4a4035', accent: '#5f5445', text: '#d0c4b0', category: 'metal' },
  { id: 'copper', name: 'Copper', color: '#4d3d35', accent: '#664f44', text: '#d4c0b4', category: 'metal' },
  { id: 'steel', name: 'Steel', color: '#404548', accent: '#555b60', text: '#c4c8cc', category: 'metal' },
];

/**
 * Get a badge by ID
 */
export function getBadgeById(id: string): Badge | undefined {
  return BADGES.find(b => b.id === id);
}

/**
 * Get the default badge for a given index (for initial assignment)
 */
export function getDefaultBadge(index: number): Badge {
  return BADGES[index % BADGES.length];
}

/**
 * Get badges grouped by category
 */
export function getBadgesByCategory(): Record<Badge['category'], Badge[]> {
  return BADGES.reduce((acc, badge) => {
    if (!acc[badge.category]) {
      acc[badge.category] = [];
    }
    acc[badge.category].push(badge);
    return acc;
  }, {} as Record<Badge['category'], Badge[]>);
}

/**
 * Category display names
 */
export const CATEGORY_NAMES: Record<Badge['category'], string> = {
  earth: 'Earth',
  water: 'Water',
  fire: 'Fire',
  air: 'Air',
  arcane: 'Arcane',
  nature: 'Nature',
  shadow: 'Shadow',
  metal: 'Metal',
};

/**
 * Available badge icons for player selection
 * Grouped by theme for easier browsing
 */
export const BADGE_ICONS: { id: IconName; label: string; category: string }[] = [
  // Combat
  { id: 'sword', label: 'Sword', category: 'Combat' },
  { id: 'shield', label: 'Shield', category: 'Combat' },
  { id: 'attack', label: 'Axe', category: 'Combat' },
  { id: 'target', label: 'Target', category: 'Combat' },

  // Magic
  { id: 'wand', label: 'Wand', category: 'Magic' },
  { id: 'sparkle', label: 'Sparkle', category: 'Magic' },
  { id: 'crystal', label: 'Crystal', category: 'Magic' },
  { id: 'moon', label: 'Moon', category: 'Magic' },
  { id: 'star', label: 'Star', category: 'Magic' },

  // Nature
  { id: 'heart', label: 'Heart', category: 'Nature' },
  { id: 'flame', label: 'Flame', category: 'Nature' },
  { id: 'eye', label: 'Eye', category: 'Nature' },

  // Dark
  { id: 'skull', label: 'Skull', category: 'Dark' },
  { id: 'horn', label: 'Horn', category: 'Dark' },

  // Symbols
  { id: 'crown', label: 'Crown', category: 'Symbols' },
  { id: 'token', label: 'Token', category: 'Symbols' },
  { id: 'scroll', label: 'Scroll', category: 'Symbols' },
  { id: 'dice', label: 'Dice', category: 'Symbols' },
];

/**
 * Get badge icons grouped by category
 */
export function getBadgeIconsByCategory(): Record<string, typeof BADGE_ICONS> {
  return BADGE_ICONS.reduce((acc, icon) => {
    if (!acc[icon.category]) {
      acc[icon.category] = [];
    }
    acc[icon.category].push(icon);
    return acc;
  }, {} as Record<string, typeof BADGE_ICONS>);
}
